<template>
  <div class="rule-container">
    <div class="delete-icon">
      <svg @click="deleteRule" class="trash-icon">
        <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#trash" />
      </svg>
    </div>

    <div class="clause">
      <TagBox value="if" />
      <div class="condition">
        <ConditionGroupBuilder
          :value="providedRule.conditions"
          :manager="ruleConditionsManager"
          :disabled="false"
          context="condition"
          @updated="conditionUpdated"
        />
        <p v-if="$v.providedRule.conditions.conditions.$invalid && $v.providedRule.$anyDirty" class="help is-danger">
          Please provide a condition
        </p>
      </div>
    </div>

    <div class="action">
      <TagBox value="then" />
      <div class="condition">
        <CapableAppPicker
          :modalHeading="'Bot Apps'"
          :capability="'botActions'"
          :selectedApps="getSelectedFollowUpApps()"
          :configRequest="configRequest"
          :isLozengeMode="true"
          @change="actionsChanged"
        />
        <p v-if="$v.providedRule.actions.$invalid && $v.providedRule.$anyDirty" class="help is-danger">
          Please provide an action
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import ConditionGroupBuilder, { ConditionGroupUpdate } from '@/shared/components/conditions/ConditionGroupBuilder.vue';
import CapableAppPicker from '@/shared/components/capableAppPicker/CapableAppPicker.vue';
import { ConfiguredAppIntegration } from '@/features/apps/shared/models/configuredAppIntegration';
import { botsApi } from '../../services/bots';
import { RuleViewModel } from '../../models/ruleViewModel';
import { required, minLength } from '@vuelidate/validators';
import TagBox from '@/shared/components/TagBox.vue';
import { FollowUpActionViewModel, FollowUpActionType, BotActionAppViewModel } from '../../models';
import { PropType, ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { IConditionsManager } from '@shared/components/conditions/models/conditionsManager';

const props = defineProps({
  rule: {
    type: Object as PropType<RuleViewModel>,
    required: true,
  },
  ruleConditionsManager: {
    type: Object as PropType<IConditionsManager>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'updated', rule: RuleViewModel, invalid: boolean): void;
  (e: 'deleted'): void;
}>();

const hasInvalidConditions = ref(false);
const providedRule = ref<RuleViewModel>({
  conditions: {
    type: 'and',
    conditions: [],
  },
  actions: [],
});

const validations = {
  providedRule: {
    required,
    conditions: {
      conditions: {
        required,
        minLength: minLength(1),
      },
    },
    actions: {
      required,
      minLength: minLength(1),
    },
  },
};

const $v = useVuelidate(validations, { providedRule });

watch(
  () => props.rule,
  (value) => {
    if (value) {
      providedRule.value = value;
    }
  },
  { immediate: true, deep: true },
);

function conditionUpdated(update: ConditionGroupUpdate) {
  hasInvalidConditions.value = update.invalid;
  const clone = JSON.parse(JSON.stringify(providedRule.value)); // to prevent updating the providedRule which would kick off processing in child
  clone.conditions = update.conditionGroup;
  emit('updated', clone, hasInvalidConditions.value);
}

function actionsChanged(selectedApps: BotActionAppViewModel[]) {
  const appActions = selectedApps.map<FollowUpActionViewModel>((app) => ({
    type: FollowUpActionType.AppIntegration,
    app,
  }));
  providedRule.value.actions = appActions;
  emit('updated', providedRule.value, hasInvalidConditions.value);
}

function configRequest(
  workspaceId: string,
  installedAppId: string,
  values: any,
  previousValues: any,
  componentId: any,
) {
  return botsApi.botAppConfig(workspaceId, installedAppId, values, previousValues, componentId);
}

function deleteRule() {
  emit('deleted');
}

function getSelectedFollowUpApps(): Array<ConfiguredAppIntegration> {
  return providedRule.value.actions
    .filter((action) => action.type === FollowUpActionType.AppIntegration)
    .filter((action) => action.app !== undefined)
    .map((action) => action.app);
}
</script>

<style scoped lang="scss">
.rule-container {
  position: relative;
  border: 1px rgb(226, 226, 226) solid;
  border-radius: 0.25rem;
  padding: 20px;
  padding-right: 40px;

  .clause {
    display: flex;
    gap: 30px;
    margin-bottom: 15px;
  }

  .action {
    display: flex;
    gap: 30px;
  }

  .delete-icon {
    position: absolute;
    right: 10px;
    top: 10px;
  }

  .trash-icon {
    width: 1.25em;
    height: 1.25em;
    fill: #c7cdd3;
    cursor: pointer;

    &:hover {
      fill: #a4a6a9;
    }
  }
}
</style>
